import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import Container from "react-bootstrap/Container";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Accordion from "react-bootstrap/Accordion";

import Shop from "../services/shopService";
import { isAdmin, isAdminOrIsService } from "../services/authService";
import Badge from "react-bootstrap/Badge";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import Pagination from "./common/pagination";
import { searchInShopTable } from "../utils/search";
import { paginate } from "../utils/paginate";

import ShopsTable from "./shopsTable";

import _ from "lodash";
import SearchBox from "./common/searchBox";
import {
  findShopsInUseDuringLastHour,
  findShopsWithDevices,
  findShopsWithDevicesPlayingPlaylists,
  findShopsWithSelectedPlaylists
} from "../utils/select";
import { isShopClosed, isShopInRenovation, isShopOpen, filterShops } from "../utils/shopManagerData";

const containerStyle = {
  width: "100%",
  height: "400px",
  margin: "auto",
};

const Shops = () => {
  // Każde pole stanu jako osobny useState
  const [filteredShops, setFilteredShops] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyWithDevices, setOnlyWithDevices] = useState(false);
  const [open, setOpen] = useState(true);
  const [close, setClose] = useState(true);
  const [renovation, setRenovation] = useState(true);
  const [cmsSetting, setCmsSetting] = useState(false);
  const [imsSetting, setImsSetting] = useState(false);
  const [noPlaylist, setNoPlaylist] = useState(false);
  const [cmsPlaying, setCmsPlaying] = useState(false);
  const [imsPlaying, setImsPlaying] = useState(false);
  const [noPlaying, setNoPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    data: getAllShopsAndDevices,
    isLoading: shopsLoading,
    refetch,
  } = useQuery({
    queryKey: ["shops"],
    queryFn: async () => {
      const { data } = await Shop.getAllShopsAndDevices();
      return data;
    },
    // notifyOnChangeProps: ["data"],
    refetchInterval: 60000,
  });

  useEffect(() => {
    console.log("mount");
    filterData();
  }, [getAllShopsAndDevices]);

  const handleFilter = async () => {
    toast
      .promise(
        async () => {
          await refetch();
          filterData();
        },
        {
          pending: "Trwa filtrowanie...",
          success: "Zakończono filtrowanie 👌",
          error: "Błąd podczas filtrowania !!!",
        },
        { autoClose: 1000, pauseOnFocusLoss: false }
      )
      .catch(() => {
        setIsLoading(false);
      });
  };

  const filterData = async () => {
    setIsLoading(true);

    let filteredShops = [];

    // toast.promise(
    //   async () => {
    //     const { data } = await Shop.getAllShopsAndDevices();
    filteredShops = getAllShopsAndDevices ? [...getAllShopsAndDevices.allShops] : [];

    if (onlyWithDevices) {
      filteredShops = findShopsWithDevices(filteredShops, getAllShopsAndDevices.devices);
    }

    filteredShops = filterShops(filteredShops, {
      showInRenovation: renovation,
      showClosed: close,
      showOpen: open,
    });

    if(cmsSetting || imsSetting || noPlaylist) {
      const playlistsSources = [...(cmsSetting ? ["CMS"] : []), ...(imsSetting ? ["IMS"] : [])];
      filteredShops = findShopsWithSelectedPlaylists(filteredShops, getAllShopsAndDevices.devices, playlistsSources, noPlaylist);
    }

    if (cmsPlaying || imsPlaying || noPlaying) {
      filteredShops = findShopsWithDevicesPlayingPlaylists(
        filteredShops,
        getAllShopsAndDevices.devices,
        [...(cmsPlaying ? ["CMS"] : []), ...(imsPlaying ? ["IMS"] : [])],
        noPlaying
      );
    }

    setFilteredShops(filteredShops);

    setIsLoading(false);
    //   },
    //   {
    //     pending: "Trwa filtrowanie...",
    //     success: "Zakończono filtrowanie 👌",
    //     error: "Błąd podczas filtrowania !!!",
    //   },
    //   { autoClose: 1000 ,pauseOnFocusLoss: false}
    // ).catch(() => {
    //   setIsLoading(false);
    // });
  };

  // useEffect(() => {
  //   console.log("mount");
  //   filterData();
  // }, []);

  const handleEdit = useCallback((event, shop) => {
    event.stopPropagation();
    window.open("/shops/" + shop._id, "_blank");
  }, []);

  const handleRowClick = useCallback((shop) => {
    window.open("/shopView/" + shop._id, "_blank");
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleSort = useCallback((newSortColumn) => {
    if (newSortColumn.path === "") return;
    setSortColumn(newSortColumn);
  }, []);

  const getPagedData = useCallback(() => {
    let filtered = filteredShops;

    if (searchQuery) {
      filtered = searchInShopTable(filteredShops, ["name", "city", "adress"], searchQuery);
    }

    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
    const pagedShops = paginate(sorted, currentPage, pageSize);

    if (filtered.length * pageSize < (currentPage - 1) * pageSize) {
      setCurrentPage(1);
    }
    return { totalCount: filtered.length, data: pagedShops };
  }, [filteredShops, searchQuery, sortColumn, currentPage, pageSize]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const { totalCount, data: displayedShops } = getPagedData();

  return (
    <React.Fragment>
      <Breadcrumb style={{ backgroundColor: "#DDDDDD" }}>
        <Breadcrumb.Item
          style={{ marginTop: 3, marginLeft: 3, marginBottom: 3, marginRight: 0 }}
          active
        >
          Drogerie
        </Breadcrumb.Item>
      </Breadcrumb>
      <Container>
        <Row
          sm={12}
          className="pt-2 d-flex flex-row align-items-center justify-content-center"
        >
          <Col sm="auto">
            {isAdminOrIsService() && (
              <Link
                className="btn btn-primary margin-bottom-3"
                to="/shops/new"
              >
                Dodaj
              </Link>
            )}
          </Col>

          <Col style={{ textAlign: "center" }}>
            <Accordion defaultActiveKey="1">
              <Accordion.Item
                eventKey="0"
                className="border-primary border-"
              >
                <Accordion.Header>
                  <div className="w-100 text-center">
                    <span className="fw-bold">Filtrowanie  (Znaleziono: {filteredShops.length})</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col md="3">
                      <table>
                        <thead>
                          <tr>
                            <th colSpan={2}>Status sklepu:</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ justifyContent: "center" }}>
                            <td className="m-0 p-0">
                              <Badge
                                bg="success"
                                className="w-100 m-0"
                              >
                                OTWARTE
                              </Badge>
                            </td>
                            <td>
                              <input
                                className="mt-2 ms-2 p-0"
                                style={{ height: "20px", width: "20px" }}
                                name="open"
                                type="checkbox"
                                checked={open}
                                onChange={() => setOpen(!open)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <Badge
                                bg="danger"
                                className="w-100"
                              >
                                ZAMKNIĘTE
                              </Badge>
                            </td>
                            <td>
                              <input
                                className="mt-2 ms-2"
                                style={{ height: "20px", width: "20px" }}
                                name="close"
                                type="checkbox"
                                checked={close}
                                onChange={() => setClose(!close)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <Badge
                                bg="warning"
                                text="dark"
                                className="w-100"
                              >
                                W REMONCIE
                              </Badge>
                            </td>
                            <td>
                              <input
                                className="mt-2 ms-2"
                                style={{ height: "20px", width: "20px" }}
                                name="renovation"
                                type="checkbox"
                                checked={renovation}
                                onChange={() => setRenovation(!renovation)}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                    <Col md="3">
                      <table style={{ justifyContent: "center", margin: "0px", padding: "0px" }}>
                        <thead>
                          <tr>
                            <th colSpan={2}>Ustawiona muzyka:</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { name: "CMS", checked: cmsSetting, setChecked: setCmsSetting },
                            { name: "IMS", checked: imsSetting, setChecked: setImsSetting },
                            { name: "BRAK", checked: noPlaylist, setChecked: setNoPlaylist },
                          ].map((item) => (
                            <tr
                              style={{ justifyContent: "center" }}
                              key={item.name + "_conf"}
                            >
                              <td>
                                <Badge
                                  bg="secondary"
                                  className="w-100"
                                  style={item.style}
                                >
                                  {item.name}
                                </Badge>
                              </td>
                              <td>
                                <input
                                  className="mt-2 ms-2 p-0"
                                  style={{ height: "20px", width: "20px" }}
                                  name={item.name.toLowerCase()}
                                  type="checkbox"
                                  checked={item.checked}
                                  onChange={() => item.setChecked(!item.checked)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Col>
                    <Col
                      md="3"
                      style={{ justifyContent: "center", margin: "0px", padding: "0px" }}
                    >
                      <table style={{ justifyContent: "center", margin: "0px", padding: "0px" }}>
                        <thead>
                          <tr>
                            <th colSpan={2}>Aktualnie odtwarza:</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { name: "CMS", checked: cmsPlaying, setChecked: setCmsPlaying },
                            { name: "IMS", checked: imsPlaying, setChecked: setImsPlaying },
                            { name: "STOP", checked: noPlaying, setChecked: setNoPlaying, style: { color: "BLACK" } },
                          ].map((item) => (
                            <tr
                              style={{ justifyContent: "center" }}
                              key={item.name}
                            >
                              <td>
                                <Badge
                                  bg="secondary"
                                  className="w-100"
                                  style={item.style}
                                >
                                  {item.name}
                                </Badge>
                              </td>
                              <td>
                                <input
                                  className="mt-2 ms-2 p-0"
                                  style={{ height: "20px", width: "20px" }}
                                  name={item.name.toLowerCase()}
                                  type="checkbox"
                                  checked={item.checked}
                                  onChange={() => item.setChecked(!item.checked)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Col>
                    <Col
                      md="3"
                      style={{ justifyContent: "center", margin: "0px", padding: "0px" }}
                    >
                      <table>
                        <thead>
                          <tr>
                            <th>Tylko z urządzeniami</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <input
                                className="m-2"
                                name="onlyWithDevices"
                                type="checkbox"
                                checked={onlyWithDevices}
                                onChange={() => setOnlyWithDevices(!onlyWithDevices)}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <button
                        className="btn btn-primary p-0 w-100 mt-2"
                        onClick={handleFilter}
                        disabled={isLoading}
                      >
                        {isLoading ? "Filtrowanie..." : "Filtruj"}
                      </button>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>

          {/* <Col
            sm="2"
            style={{ textAlign: "center" }}
          >
            <label>Liczba drogerii: {filteredShops.length}</label>
          </Col> */}
          <Col sm="2">
            {isAdmin() && (
              <Link
                className="btn btn-primary "
                to="/shopsImport"
              >
                Zaimportuj z pliku .xlsx
              </Link>
            )}
          </Col>
        </Row>

        <Row className={"pt-3 d-flex flex-row align-items-center justify-content-center"}>
          <Col
            ms="12"
            xs="12"
            className="w-75"
          >
            <SearchBox
              value={searchQuery}
              onChange={handleSearch}
            />
          </Col>
        </Row>
        <Row className="mt-2 mb-0">
          <Col
            ms="12"
            xs="12"
            className="mt-0 mb-0"
          >
            <Pagination
              itemsCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </Col>
        </Row>
        <Row className={"mt-1"}>
          <Col
            ms="12"
            xs="12"
          >
            <ShopsTable
              shops={displayedShops}
              sortColumn={sortColumn}
              onEdit={handleEdit}
              onSort={handleSort}
              onRowClick={handleRowClick}
            />
          </Col>
        </Row>

        <Row className="mb-1">
          <Col
            ms="12"
            xs="12"
          >
            <Pagination
              itemsCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Shops;
